/**
 * src/components/legal/LegalContent.tsx
 * =============================================================================
 * Cuerpo de los tres documentos legales (Términos, Privacidad, Cookies).
 * Cada uno se exporta como componente sin layout — los consumidores (la
 * página /legal/* dedicada y el LegalModal) los envuelven con su propio
 * chrome (cabecera, footer, banner de borrador).
 *
 * Sin clases de styling aquí: los wrappers aplican estilos a h2/h3/p/ul vía
 * arbitrary selectors [&_h2]:* — así el contenido es portátil entre el
 * layout de página completa y el modal con el mismo aspecto.
 *
 * ⚠️ DATOS IDENTIFICATIVOS PENDIENTES — LEER ANTES DE COBRAR
 * ----------------------------------------------------------
 * Estado 2026-08-14: los placeholders `{{empresa}} {{NIF}} {{dirección}}`
 * se han sustituido por la marca `Ploter` y el contacto real
 * `mail@ploter.me`. Es un interino consciente: el usuario aún no tiene SL
 * (la constituirá en meses) y no quiere publicar su nombre todavía.
 *
 * Esto es defendible SOLO mientras esto sea una beta cerrada sin cobrar.
 * Hay que completarlo con datos reales (nombre o denominación + NIF +
 * domicilio) en cuanto ocurra CUALQUIERA de estas dos cosas:
 *
 *   1. Se registre el primer usuario que no sea el propio dueño → a partir
 *      de ahí se tratan datos personales ajenos y el RGPD exige identificar
 *      al responsable del tratamiento con identidad real.
 *   2. Se cobre el primer euro → la LSSI-CE art. 10 lo exige sin margen.
 *
 * Matiz importante: Polar actúa como Merchant of Record y resuelve la
 * factura y el IVA, pero NO el rol de responsable del tratamiento. Los PDFs
 * y los emails los guarda Ploter, así que esa obligación no se delega.
 *
 * `Ploter` es una marca, no una persona jurídica: hasta que exista la SL,
 * el responsable legal es la persona física que opera el servicio. El
 * domicilio SÍ puede ser el de una gestoría o un coworking en vez del
 * particular — es habitual y legítimo.
 *
 * Buscar `DATOS-PENDIENTES` en este archivo para localizar los dos puntos
 * exactos que hay que completar (Términos § 1 y Privacidad § 1). El correo
 * de contacto ya es real y no necesita cambio.
 *
 * Pendiente además: revisión por abogado especialista en RGPD.
 * =============================================================================
 */

export function TermsContent() {
  return (
    <>
      <h2>1. Identificación del prestador</h2>
      <p>
        Estos Términos y condiciones (en adelante, "Términos") regulan el acceso y uso de
        la plataforma <strong>Ploter</strong> (en adelante, "el Servicio"), accesible a
        través de los dominios ploter.me y subdominios asociados.
      </p>
      {/* DATOS-PENDIENTES (1 de 2): añadir denominación, NIF y domicilio. */}
      <p>
        El Servicio es prestado por <strong>Ploter</strong> (en adelante, "el
        Prestador"), que puede ser contactado en{' '}
        <strong>mail@ploter.me</strong> para cualquier cuestión relativa a
        estos Términos.
      </p>

      <h2>2. Objeto y aceptación</h2>
      <p>
        Ploter es una plataforma SaaS para subir, revisar, comentar y compartir documentos
        PDF (planos, diseños, contratos, presupuestos, presentaciones, propuestas e informes),
        con anotaciones colaborativas en tiempo real.
      </p>
      <p>
        El uso del Servicio implica la aceptación plena y sin reservas de estos Términos.
        Si el usuario no está de acuerdo con alguno de los puntos, debe abstenerse de
        utilizar el Servicio.
      </p>

      <h2>3. Cuenta y registro</h2>
      <p>
        Para acceder a funcionalidades avanzadas (documentos sin caducidad, espacios
        compartidos) se requiere la creación de una cuenta proporcionando un correo
        electrónico válido y una contraseña. El usuario se compromete a facilitar
        información veraz y mantenerla actualizada.
      </p>
      <p>
        El uso anónimo (sin cuenta) está permitido para los enlaces de compartición de
        un único documento, con caducidad automática (7 días por defecto).
      </p>

      <h2>4. Uso aceptable</h2>
      <p>El usuario se compromete a no utilizar el Servicio para:</p>
      <ul>
        <li>Realizar actividades ilegales o que vulneren derechos de terceros.</li>
        <li>Subir contenido ofensivo, difamatorio o que infrinja derechos de propiedad intelectual.</li>
        <li>Intentar acceder a sistemas o datos no autorizados.</li>
        <li>Automatizar el uso del Servicio mediante bots o scripts no autorizados.</li>
        <li>Compartir credenciales o ceder el acceso a terceros no autorizados.</li>
      </ul>

      <h2>5. Propiedad intelectual</h2>
      <p>
        El usuario conserva todos los derechos sobre los documentos que sube al Servicio.
        Otorga al Prestador una licencia limitada, no exclusiva y no transferible para
        procesar su contenido únicamente con la finalidad de prestar el Servicio
        (almacenamiento, render del PDF, sincronización de anotaciones). El Prestador NO
        utilizará el contenido del usuario para entrenar modelos propios ni de terceros.
      </p>

      <h2>6. Suscripción y pagos</h2>
      <p>
        Las modalidades de suscripción y precios se publican en la página oficial. Los
        pagos se procesan a través de Polar.sh actuando como <em>merchant of record</em>.
        El usuario podrá cancelar su suscripción en cualquier momento; la cancelación
        surte efecto al final del periodo de facturación en curso.
      </p>

      <h2>7. Limitación de responsabilidad</h2>
      <p>
        El Servicio se presta "tal cual". El Prestador no garantiza la disponibilidad
        continua del Servicio ni la ausencia total de errores. En la máxima medida
        permitida por la legislación aplicable, la responsabilidad del Prestador frente
        al usuario se limita al importe equivalente a las cuotas pagadas por el usuario
        en los 12 meses anteriores al hecho causante.
      </p>

      <h2>8. Modificaciones</h2>
      <p>
        El Prestador puede modificar estos Términos. Las modificaciones se comunicarán
        con al menos 30 días de antelación al usuario por correo electrónico o aviso en
        la plataforma.
      </p>

      <h2>9. Ley aplicable y jurisdicción</h2>
      <p>
        Estos Términos se rigen por la legislación española. Cualquier controversia
        derivada de los mismos se someterá a los Juzgados y Tribunales del domicilio del
        usuario consumidor, o al del Prestador en caso de usuarios profesionales, salvo
        norma imperativa que disponga otra cosa.
      </p>
    </>
  )
}

export function PrivacyContent() {
  return (
    <>
      <h2>1. Responsable del tratamiento</h2>
      {/* DATOS-PENDIENTES (2 de 2): el RGPD exige identificar al responsable
          con nombre o denominación, NIF y domicilio reales. Obligatorio en
          cuanto haya un usuario que no sea el dueño. */}
      <p>
        El responsable del tratamiento es <strong>Ploter</strong>, con correo
        electrónico de contacto <strong>mail@ploter.me</strong>.
      </p>
      <p>
        En los términos del RGPD y la LOPDGDD, el responsable se compromete a tratar los
        datos personales conforme a los principios de licitud, lealtad, transparencia,
        limitación de la finalidad, minimización, exactitud, limitación del plazo de
        conservación, integridad y confidencialidad.
      </p>

      <h2>2. Datos personales que recogemos</h2>
      <ul>
        <li><strong>Datos de cuenta:</strong> email, contraseña cifrada (hash), nombre.</li>
        <li><strong>Datos de uso:</strong> identificadores de sesión, IP truncada, User-Agent.</li>
        <li><strong>Contenido subido:</strong> archivos PDF y anotaciones que el usuario crea.</li>
        <li><strong>Datos de facturación:</strong> nombre, dirección fiscal, NIF/CIF, datos de pago tokenizados por el proveedor.</li>
      </ul>

      <h2>3. Finalidades del tratamiento</h2>
      <ul>
        <li>Prestación del Servicio: autenticación, almacenamiento, sincronización de anotaciones.</li>
        <li>Gestión administrativa y contable, emisión de facturas.</li>
        <li>Soporte al usuario.</li>
        <li>Detección de fraude y aplicación de límites de uso.</li>
        <li>Cumplimiento de obligaciones legales.</li>
      </ul>
      <p>
        El Servicio <strong>NO</strong> utiliza el contenido subido para entrenar modelos
        de IA propios ni de terceros.
      </p>

      <h2>4. Base jurídica</h2>
      <ul>
        <li><strong>Ejecución de un contrato</strong> (art. 6.1.b RGPD) — para prestar el Servicio.</li>
        <li><strong>Cumplimiento de obligación legal</strong> (art. 6.1.c RGPD) — facturación, auditoría.</li>
        <li><strong>Interés legítimo</strong> (art. 6.1.f RGPD) — seguridad y prevención de fraude.</li>
        <li><strong>Consentimiento</strong> (art. 6.1.a RGPD) — cookies o tratamientos no estrictamente necesarios.</li>
      </ul>

      <h2>5. Plazos de conservación</h2>
      <ul>
        <li><strong>Documentos compartidos anónimos:</strong> 7 días (TTL del plan gratuito).</li>
        <li><strong>Datos de cuenta y contenido de pago:</strong> mientras la cuenta esté activa. Tras la baja se eliminan en máximo 30 días.</li>
        <li><strong>Datos de facturación:</strong> seis (6) años, conforme al Código de Comercio.</li>
        <li><strong>Logs técnicos:</strong> hasta 12 meses.</li>
      </ul>

      <h2>6. Encargados del tratamiento</h2>
      <ul>
        {/* Desde el 2026-09-08 todo el alojamiento está en OVH (Francia, UE).
            Cloudflare dejó de ser encargado: solo conserva temporalmente la
            gestión de los servidores de nombres (DNS), que no trata datos
            personales de los usuarios y por eso no se lista aquí. */}
        <li><strong>OVH SAS</strong> (Francia, UE) — alojamiento de la aplicación y de la base de datos PostgreSQL en un servidor privado virtual situado en Estrasburgo (Francia), y almacenamiento de los archivos subidos en OVH Object Storage (región SBG, Estrasburgo). Los datos alojados no salen del Espacio Económico Europeo.</li>
        <li><strong>Polar Software, Inc.</strong> (USA) — procesamiento de pagos y facturación como Merchant of Record. Recoge datos de tarjeta y emite la factura.</li>
        <li><strong>Resend, Inc.</strong> (USA) — envío de emails transaccionales (verificación de cuenta, restablecimiento de contraseña).</li>
      </ul>

      <h2>7. Transferencias internacionales</h2>
      <p>
        El alojamiento de la aplicación, de la base de datos y de los archivos subidos se
        realiza íntegramente en la Unión Europea, por lo que no implica transferencia
        internacional de datos. Las únicas transferencias a EE. UU. son las derivadas de
        los encargados establecidos en ese país indicados en el apartado anterior, y se
        realizan al amparo de las Cláusulas Contractuales Tipo aprobadas por la Comisión
        Europea (Decisión 2021/914) y, cuando aplique, del EU-U.S. Data Privacy Framework.
      </p>

      <h2>8. Derechos del interesado</h2>
      <p>
        El usuario puede ejercer en cualquier momento sus derechos de acceso, rectificación,
        supresión, oposición, limitación y portabilidad escribiendo a{' '}
        <strong>mail@ploter.me</strong>. Si considera que sus derechos no se atienden
        adecuadamente, puede reclamar ante la{' '}
        <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">Agencia
        Española de Protección de Datos</a>.
      </p>

      <h2>9. Medidas de seguridad</h2>
      <ul>
        <li>Cifrado TLS 1.2+ en todas las comunicaciones.</li>
        <li>Cifrado en reposo de base de datos y almacenamiento.</li>
        <li>Aislamiento estricto entre cuentas mediante comprobaciones de membresía en cada consulta del servidor.</li>
        <li>Autenticación con contraseña cifrada (hash + sal).</li>
        <li>Registro de auditoría de operaciones sensibles.</li>
      </ul>

      <h2>10. Modificaciones</h2>
      <p>
        Esta política puede modificarse para adaptarse a cambios legales o técnicos. Las
        modificaciones sustanciales se comunicarán con al menos 30 días de antelación.
      </p>
    </>
  )
}

export function CookiesContent() {
  return (
    <>
      <h2>1. Qué son las cookies</h2>
      <p>
        Las cookies son pequeños archivos que un sitio web almacena en el dispositivo del
        usuario al visitarlo. Permiten reconocerlo y guardar preferencias o información
        de sesión. El término se utiliza también, por extensión, para tecnologías
        equivalentes como el almacenamiento local del navegador.
      </p>

      <h2>2. Qué cookies utiliza Ploter</h2>
      <p>
        El Servicio utiliza únicamente cookies y almacenamiento local{' '}
        <strong>estrictamente necesarios</strong>. No se emplean cookies de seguimiento,
        publicidad ni redes sociales.
      </p>

      <h3>2.1 Sesión y autenticación (cuenta registrada)</h3>
      <ul>
        <li><strong>better-auth.session_token</strong> — cookie HTTP-only que identifica la sesión del usuario tras el login. Cifrada y firmada por el servidor.</li>
        <li><strong>better-auth.csrf_token</strong> — token CSRF para proteger las acciones autenticadas.</li>
      </ul>

      <h3>2.2 Preferencias del usuario</h3>
      <ul>
        <li><strong>i18nextLng</strong> — idioma seleccionado (se infiere del navegador si no se especifica).</li>
        <li><strong>ploter:share:&lt;token&gt;:author</strong> — nombre con el que firma las anotaciones de cada share anónimo.</li>
        <li><strong>ploter:cookies-acknowledged</strong> — registro de que el usuario ha cerrado el aviso informativo de cookies.</li>
      </ul>

      <h2>3. Cookies de terceros</h2>
      <p>
        Cuando un usuario contrate una suscripción de pago, el proveedor de pagos puede
        instalar sus propias cookies técnicas para detección de fraude durante el flujo
        de pago. Estas cookies se rigen por la política de privacidad del proveedor.
      </p>
      <p>
        El Servicio <strong>no integra</strong> Google Analytics, Meta Pixel, Hotjar ni
        ningún otro tracker de terceros. Si en el futuro se incorporasen, se actualizaría
        esta política y se solicitaría consentimiento previo conforme al art. 22.2 LSSI.
      </p>

      <h2>4. Base jurídica</h2>
      <p>
        Las cookies descritas son <strong>técnicamente necesarias</strong> conforme al
        art. 22.2 LSSI y la Guía sobre el uso de cookies de la AEPD. No requieren
        consentimiento previo por estar destinadas exclusivamente a permitir la
        comunicación con la red o a prestar un servicio expresamente solicitado.
      </p>

      <h2>5. Cómo desactivar o eliminar las cookies</h2>
      <p>
        El usuario puede configurar o eliminar las cookies desde las opciones de su
        navegador. <strong>Desactivar las cookies de sesión impedirá iniciar sesión y
        utilizar la parte autenticada del Servicio.</strong>
      </p>
      <ul>
        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
        <li><a href="https://support.mozilla.org/es/kb/proteccion-antirrastreo-mejorada-firefox-escritorio" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
        <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
        <li><a href="https://support.microsoft.com/es-es/microsoft-edge" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
      </ul>

      <h2>6. Modificaciones</h2>
      <p>
        Esta política puede actualizarse cuando se modifiquen las tecnologías utilizadas.
        Si se incorporasen cookies que requieran consentimiento, se habilitará un
        mecanismo de gestión de consentimiento expreso.
      </p>
    </>
  )
}
